import { useOffline } from "../offline/OfflineProvider";
// UploadStatusContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getAPIBaseURL } from './api';

const API_BASE_URL = getAPIBaseURL();
const UploadStatusContext = createContext();
const UPLOAD_STATUS_STORAGE_KEY = 'uploadStatus';

const createDefaultUploadStatus = () => ({
  started: false,
  uploading: false,
  processing: false,
  completed: false,
  success: false,
  errors: {},
  processedCount: 0,
  totalCount: 0,
  queueItems: [],
});

const normalizeStatus = (status) => {
  const base = createDefaultUploadStatus();
  if (!status || typeof status !== 'object') {
    return base;
  }

  // Don't include queueItems if not explicitly provided
  // This allows server to manage queueItems without client interference
  const normalizedQueueItems = 'queueItems' in status
    ? (Array.isArray(status.queueItems) ? [...status.queueItems] : base.queueItems)
    : base.queueItems;

  return {
    ...base,
    ...status,
    errors:
      status.errors && typeof status.errors === 'object'
        ? { ...status.errors }
        : base.errors,
    queueItems: normalizedQueueItems,
  };
};

const readPersistedStatus = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(UPLOAD_STATUS_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    return normalizeStatus(JSON.parse(stored));
  } catch (error) {
    console.error('Failed to read persisted upload status:', error);
    return null;
  }
};

export const useUploadStatus = () => useContext(UploadStatusContext);

export const UploadStatusProvider = ({ children }) => {
  const [uploadStatus, setUploadStatus] = useState(() => {
    const persisted = readPersistedStatus();
    return persisted || createDefaultUploadStatus();
  });

  const eventSourceRef = useRef(null);
  const { isOnline } = useOffline();

  useEffect(() => {
    // Offline: don't open the stream — the browser's native EventSource
    // reconnect would loop every ~3 s. Re-created when connectivity returns.
    if (!isOnline) { eventSourceRef.current = null; return undefined; }
    // Create EventSource for Server-Sent Events (SSE)
    // This replaces the old polling mechanism with real-time updates
    console.log('[UploadStatusContext SSE] Connecting to SSE endpoint...', {
      url: `${API_BASE_URL}/api/uploadStatus/stream`,
      API_BASE_URL: API_BASE_URL || '(empty - relative path)'
    });

    const eventSource = new EventSource(`${API_BASE_URL}/api/uploadStatus/stream`, {
      withCredentials: true
    });

    eventSourceRef.current = eventSource;

    // Handle incoming status updates
    eventSource.onmessage = (event) => {
      try {
        const status = JSON.parse(event.data);
        console.log('[UploadStatusContext SSE] Received update:', {
          uploading: status.uploading,
          processing: status.processing,
          completed: status.completed,
          success: status.success,
          queueLength: status.queueItems?.length || 0,
        });
        const normalized = normalizeStatus(status);
        console.log('[UploadStatusContext SSE] After normalize:', {
          uploading: normalized.uploading,
          processing: normalized.processing,
          completed: normalized.completed,
        });
        setUploadStatus(normalized);
      } catch (error) {
        console.error('[UploadStatusContext SSE] Parse error:', error);
      }
    };

    // Handle connection open
    eventSource.onopen = () => {
      console.log('[UploadStatusContext SSE] Connection established successfully');
    };

    // Handle errors (EventSource will automatically try to reconnect)
    eventSource.onerror = (error) => {
      console.error('[UploadStatusContext SSE] Connection error:', error);
      console.log('[UploadStatusContext SSE] ReadyState:', eventSource.readyState,
        eventSource.readyState === 0 ? '(CONNECTING)' :
        eventSource.readyState === 1 ? '(OPEN)' : '(CLOSED)');
    };

    // Cleanup on unmount
    return () => {
      console.log('[UploadStatusContext SSE] Closing SSE connection...');
      eventSource.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]); // re-created when connectivity changes

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    try {
      window.localStorage.setItem(
        UPLOAD_STATUS_STORAGE_KEY,
        JSON.stringify(uploadStatus)
      );
    } catch (error) {
      console.error('Failed to persist upload status:', error);
    }
  }, [uploadStatus]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event) => {
      if (event.key !== UPLOAD_STATUS_STORAGE_KEY) {
        return;
      }

      if (!event.newValue) {
        setUploadStatus(createDefaultUploadStatus());
        return;
      }

      try {
        const parsed = JSON.parse(event.newValue);
        setUploadStatus(normalizeStatus(parsed));
      } catch (error) {
        console.error('Failed to sync upload status from storage:', error);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Update the server's upload status.
  // The server will broadcast this to all connected SSE clients automatically.
  const updateUploadStatus = async (newStatus) => {
    const normalizedStatus = normalizeStatus(newStatus);
    // Optimistically update local state, but preserve server-managed queueItems
    // Only update queueItems if explicitly provided by the client
    // SSE will provide the authoritative queueItems from the server
    // However, NEVER let empty queueItems from SSE overwrite existing client items
    setUploadStatus(prev => ({
      ...normalizedStatus,
      queueItems: ('queueItems' in newStatus)
        ? (normalizedStatus.queueItems.length > 0
            ? normalizedStatus.queueItems  // SSE has items, use them
            : prev.queueItems)             // SSE is empty, keep existing
        : prev.queueItems                   // queueItems not in update, keep existing
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/updateUploadStatus`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(normalizedStatus),
      });

      if (response.ok) {
        const result = await response.json();
        // console.log(`Upload status updated, notified ${result.clientsNotified} client(s)`);
      }
      // No need to manually fetch - SSE will push the update to us
    } catch (error) {
      console.error("Error updating upload status:", error);
    }

    return normalizedStatus;
  };

  // Cancel an upload item by ID
  const cancelUploadItem = async (itemId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/uploadStatus/cancel/${itemId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        // console.log(`Upload item ${itemId} cancelled:`, result.message);
        return true;
      } else {
        const error = await response.json();
        console.error(`Failed to cancel upload item ${itemId}:`, error.error);
        return false;
      }
    } catch (error) {
      console.error("Error cancelling upload item:", error);
      return false;
    }
  };

  // Cancel all active upload items (pending or processing)
  const cancelAllUploads = async () => {
    const activeItems = uploadStatus.queueItems.filter(
      item => item.status === 'pending' || item.status === 'processing'
    );

    if (activeItems.length === 0) {
      return { success: true, cancelledCount: 0 };
    }

    const results = await Promise.allSettled(
      activeItems.map(item => cancelUploadItem(item.id))
    );

    const cancelledCount = results.filter(r => r.status === 'fulfilled' && r.value).length;

    return {
      success: cancelledCount > 0,
      cancelledCount,
      totalAttempted: activeItems.length
    };
  };

  return (
    <UploadStatusContext.Provider value={{ uploadStatus, updateUploadStatus, cancelUploadItem, cancelAllUploads }}>
      {children}
    </UploadStatusContext.Provider>
  );
};
